import { default as React, useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  InfoCard,
  Table,
  TableColumn
} from '@backstage/core-components';
import {
  makeStyles,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip as MuiTooltip,
  Chip,
  TextField,
  Button,
  useTheme,
  CircularProgress,
  Box
} from '@material-ui/core';
import RefreshIcon from '@material-ui/icons/Refresh';
import FilterListIcon from '@material-ui/icons/FilterList';
import VisibilityIcon from '@material-ui/icons/Visibility';
import MoreHorizIcon from '@material-ui/icons/MoreHoriz';
import { transformGrafanaTableResponse } from '../../utils/dataTransformer';
import { QueryResponse } from '../../types/grafana';
import { getLogLevelColor } from '../../utils/chartUtils';
import LogDialog, { LogDialogEntry } from '../common/LogDialog';
import EmptyState from '../common/EmptyState';

const DEFAULT_PAGE_SIZE = 100;
const LOG_ID_PREFIX_LENGTH = 50;

interface LogEntry {
  timestamp: string;
  level: string;
  message: string;
  labels?: Record<string, string>;
  tsNs?: number;
  id?: string;
}

const useStyles = makeStyles(theme => ({
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
  },
  formControl: {
    margin: theme.spacing(0, 1),
    minWidth: 120,
  },
  noDataMessage: {
    padding: theme.spacing(4),
    textAlign: 'center',
  },
  lastRefreshed: {
    marginRight: theme.spacing(1),
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
  },
  filterContainer: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
  },
  chip: {
    margin: theme.spacing(0.5),
  },
  messageCell: {
    maxWidth: '400px',
    minWidth: '100px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  viewButtonCell: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
  },
  tableContainer: {
    overflowX: 'auto',
    width: '100%',
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(255, 255, 255, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  tableWrapper: {
    position: 'relative',
  },
  loadMoreContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(2),
    marginTop: theme.spacing(1),
  },
  loadMoreButton: {
    textTransform: 'none',
  },
}));

type LogsViewerProps = {
  data: QueryResponse;
  title: string;
  onRefresh?: () => void;
  onLoadMore?: (offset: number, limit: number) => void;
  lastRefreshed?: Date | null;
  loading?: boolean;
  hasMore?: boolean;
};

const createLogId = (log: LogEntry): string => {
  return `${log.timestamp}-${log.message.substring(0, LOG_ID_PREFIX_LENGTH)}`;
};

export const LogsViewer = ({ 
  data, 
  title, 
  onRefresh,
  onLoadMore,
  lastRefreshed,
  loading = false,
  hasMore = false,
}: LogsViewerProps) => {
  const classes = useStyles();
  const theme = useTheme();
  const [levelFilter, setLevelFilter] = useState<string[]>(['error', 'warn', 'info', 'debug']);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedLog, setSelectedLog] = useState<LogDialogEntry | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loadingState, setLoadingState] = useState<'idle' | 'loading' | 'loadingMore' | 'error'>('idle');
  
  const [loadedLogs, setLoadedLogs] = useState<LogEntry[]>([]);
  
  const loadedLogsMapRef = useRef<Map<string, boolean>>(new Map());
  
  const queryIdRef = useRef<string>('');
  
  useEffect(() => {
    if (data && data.results) {
      const queryId = Object.keys(data.results).join('-');

      if (queryId !== queryIdRef.current) {
        setLoadedLogs([]);
        loadedLogsMapRef.current.clear();
        queryIdRef.current = queryId;
      }
    }
  }, [data]);
  
  useEffect(() => {
    try {
      const savedFilters = localStorage.getItem('logLevelFilters');
      if (savedFilters) {
        setLevelFilter(JSON.parse(savedFilters));
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Erro ao carregar filtros salvos:', error);
    }
  }, []);
  
  useEffect(() => {
    try {
      localStorage.setItem('logLevelFilters', JSON.stringify(levelFilter));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Erro ao salvar filtros:', error);
    }
  }, [levelFilter]);
  
  useEffect(() => {
    if (loading) {
      setLoadingState(loadedLogs.length > 0 ? 'loadingMore' : 'loading');
    } else {
      setLoadingState('idle');
    }
  }, [loading, loadedLogs.length]);
  
  const { rows } = transformGrafanaTableResponse(data);
  
  useEffect(() => {
    if (rows && rows.length > 0) {
      const newLogs = rows.filter(row => {
        const logId = createLogId(row);

        if (loadedLogsMapRef.current.has(logId)) {
          return false;
        }

        loadedLogsMapRef.current.set(logId, true);
        return true;
      });
            
      setLoadedLogs(prevLogs => [...prevLogs, ...newLogs]);
    }
  }, [rows]);
  
  // Reset apenas quando os filtros mudam — `rows` propaga via outro efeito
  // (linha ~217); incluí-la aqui causaria reset a cada chegada de novas linhas.
  useEffect(() => {
    setLoadedLogs([]);
    loadedLogsMapRef.current.clear();

    if (rows && rows.length > 0) {
      setLoadedLogs(rows);

      rows.forEach(row => {
        const logId = createLogId(row);
        loadedLogsMapRef.current.set(logId, true);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [levelFilter, searchTerm]);
  
  const filteredLogs = useMemo(() => {
    return loadedLogs.filter(row => {
      const level = String(row.level).toLowerCase();
      const matchesLevel = levelFilter.includes(level);
      const matchesSearch = searchTerm === '' || 
        String(row.message).toLowerCase().includes(searchTerm.toLowerCase());
      return matchesLevel && matchesSearch;
    });
  }, [loadedLogs, levelFilter, searchTerm]);
  
  const sortedLogs = useMemo(() => {
    const rowsWithIds = filteredLogs.map((row, index) => ({
      ...row,
      id: row.id || `${row.timestamp}-${index}`,
    }));

    return [...rowsWithIds].sort((a, b) => {
      const dateA = new Date(a.timestamp).getTime();
      const dateB = new Date(b.timestamp).getTime();
      return dateB - dateA;
    });
  }, [filteredLogs]);
  
  const formatLastRefreshed = useCallback(() => {
    if (!lastRefreshed) return '';
    return `Atualizado em: ${lastRefreshed.toLocaleTimeString()}`;
  }, [lastRefreshed]);

  const handleViewLog = useCallback((log: LogDialogEntry) => {
    setSelectedLog(log);
    setDialogOpen(true);
  }, []);

  const handleLoadMore = useCallback(() => {
    if (onLoadMore) {
      onLoadMore(loadedLogs.length, DEFAULT_PAGE_SIZE);
    }
  }, [onLoadMore, loadedLogs.length]);
  
  const handleRefresh = useCallback(() => {
    if (onRefresh) {
      setLoadedLogs([]);
      loadedLogsMapRef.current.clear();
      onRefresh();
    }
  }, [onRefresh]);
  
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  }, []);
  
  const handleLevelFilterChange = useCallback((e: React.ChangeEvent<{ value: unknown }>) => {
    setLevelFilter(e.target.value as string[]);
  }, []);
  
  const tableColumns: TableColumn[] = useMemo(() => [
    { 
      title: 'Timestamp', 
      field: 'timestamp',
      width: '20%'
    },
    { 
      title: 'Level', 
      field: 'level',
      width: '10%',
      render: (row: any) => {
        const level = String(row.level).toLowerCase();
        return (
          <Chip 
            label={level} 
            size="small" 
            style={{ 
              backgroundColor: getLogLevelColor(level),
              color: '#fff'
            }} 
          />
        );
      }
    },
    { 
      title: 'Message', 
      field: 'message',
      width: '55%',
      render: (row: any) => (
        <div className={classes.messageCell}>
          {row.message}
        </div>
      )
    },
    {
      title: 'Ações',
      field: 'actions',
      width: '15%',
      render: (row: any) => (
        <div className={classes.viewButtonCell}>
          <IconButton 
            size="small" 
            onClick={() => handleViewLog(row)}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </div>
      )
    }
  ], [classes.messageCell, classes.viewButtonCell, handleViewLog]);
  
  if ((!rows || rows.length === 0) && loadedLogs.length === 0) {
    return (
      <InfoCard title={title}>
        <EmptyState 
          message="Nenhum log disponível para exibição" 
          onAction={onRefresh}
          actionLabel={onRefresh ? "Atualizar" : undefined}
        />
      </InfoCard>
    );
  }

  return (
    <InfoCard title={title}>
      <div className={classes.header}>
        <Typography variant="caption" className={classes.lastRefreshed}>
          {formatLastRefreshed()}
        </Typography>
        
        <div className={classes.controls}>
          <MuiTooltip title="Filtros">
            <IconButton onClick={() => setShowFilters(!showFilters)} size="small">
              <FilterListIcon />
            </IconButton>
          </MuiTooltip>
          
          {onRefresh && (
            <MuiTooltip title="Atualizar dados">
              <IconButton 
                onClick={handleRefresh} 
                size="small" 
                disabled={loadingState !== 'idle'}
              >
                <RefreshIcon />
              </IconButton>
            </MuiTooltip>
          )}
        </div>
      </div>
      
      {showFilters && (
        <div className={classes.filterContainer}>
          <TextField
            label="Pesquisar nas mensagens"
            variant="outlined"
            size="small"
            fullWidth
            value={searchTerm}
            onChange={handleSearchChange}
            margin="normal"
          />
          
          <FormControl variant="outlined" size="small" className={classes.formControl} fullWidth margin="normal">
            <InputLabel id="level-filter-label">Níveis</InputLabel>
            <Select
              labelId="level-filter-label"
              id="level-filter"
              multiple
              value={levelFilter}
              onChange={handleLevelFilterChange}
              label="Níveis"
              renderValue={(selected) => (
                <div>
                  {(selected as string[]).map((value) => (
                    <Chip 
                      key={value} 
                      label={value} 
                      className={classes.chip} 
                      size="small"
                      style={{ 
                        backgroundColor: getLogLevelColor(value),
                        color: '#fff'
                      }}
                    />
                  ))}
                </div>
              )}
            >
              <MenuItem value="error">Error</MenuItem>
              <MenuItem value="warn">Warn</MenuItem>
              <MenuItem value="info">Info</MenuItem>
              <MenuItem value="debug">Debug</MenuItem>
            </Select>
          </FormControl>
        </div>
      )}
      
      <div className={classes.tableWrapper}>
        {loadingState === 'loading' && (
          <div className={classes.loadingOverlay}>
            <CircularProgress />
          </div>
        )}
        <div className={classes.tableContainer}>
          <Table
            options={{
              paging: false,
              sorting: true,
              search: false,
              padding: 'dense',
              tableLayout: 'fixed',
              headerStyle: {
                backgroundColor: theme.palette.type === 'dark' 
                  ? theme.palette.background.paper 
                  : '#f5f5f5',
                color: theme.palette.text.primary,
                whiteSpace: 'nowrap'
              },
              rowStyle: (rowData) => {
                const isDark = theme.palette.type === 'dark';
                let backgroundColor = 'inherit';
                if (rowData.level === 'error') {
                  backgroundColor = isDark ? 'rgba(244, 67, 54, 0.2)' : 'rgba(244, 67, 54, 0.1)';
                } else if (rowData.level === 'warn') {
                  backgroundColor = isDark ? 'rgba(255, 152, 0, 0.2)' : 'rgba(255, 152, 0, 0.1)';
                }
                return { backgroundColor };
              },
            }}
            data={sortedLogs}
            columns={tableColumns}
            title={`${filteredLogs.length} logs exibidos`}
            style={{ tableLayout: 'fixed', width: '100%' }}
          />
        </div>
      </div>
      
      {hasMore && (
        <Box className={classes.loadMoreContainer}>
          <Button
            variant="outlined"
            color="primary"
            className={classes.loadMoreButton}
            onClick={handleLoadMore}
            disabled={loadingState !== 'idle'}
            startIcon={loadingState === 'loadingMore' ? <CircularProgress size={16} /> : <MoreHorizIcon />}
          >
            {loadingState === 'loadingMore' ? 'Carregando...' : 'Carregar mais logs'}
          </Button>
        </Box>
      )}
      
      <LogDialog 
        open={dialogOpen} 
        onClose={() => setDialogOpen(false)} 
        log={selectedLog} 
      />
    </InfoCard>
  );
};